import React, { useState, useEffect } from 'react';
import {
  getCompatibilityMatrix,
  getConnectorCompatibility,
  updateConnectorCompatibility,
  addConnectorToMatrix,
  updateConnectorDetails,
  deleteConnectorFromMatrix,
  importCompatibilityMatrix,
  exportCompatibilityMatrix,
  validateCompatibilityMatrix,
  getCompatibilityHistory
} from '../services/compatibilitySystemV2';

// Add admin token prompt and storage
function getAdminToken() {
  let token = localStorage.getItem('adminToken');
  if (!token) {
    token = window.prompt('Enter admin token:');
    if (token) localStorage.setItem('adminToken', token);
  }
  return token;
}

const CompatibilityMatrixAdmin = () => {
  const [matrix, setMatrix] = useState({ connectors: {} });
  const [selectedConnector, setSelectedConnector] = useState(null);
  const [connectorDetails, setConnectorDetails] = useState({});
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState('');
  const [history, setHistory] = useState([]);
  const [validation, setValidation] = useState(null);
  const [importData, setImportData] = useState('');
  const [importFormat, setImportFormat] = useState('json');
  const [replaceMode, setReplaceMode] = useState(false);
  const [newConnector, setNewConnector] = useState({
    id: '',
    type: '',
    gender: '',
    brand: '',
    description: '',
    family: '',
    pins: 0,
    skuCode: '',
    verified: false,
    compatibleWith: [],
    incompatibleWith: []
  });
  const [activeTab, setActiveTab] = useState('matrix');
  const [s3UploadFile, setS3UploadFile] = useState(null);
  const [s3UploadStatus, setS3UploadStatus] = useState('');
  const [s3DownloadStatus, setS3DownloadStatus] = useState('');

  useEffect(() => {
    loadMatrix();
    loadHistory();
  }, []);

  const loadMatrix = async () => {
    setLoading(true);
    try {
      const matrixData = await getCompatibilityMatrix();
      setMatrix(matrixData);
    } catch (error) {
      setNotification('Error loading compatibility matrix: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      const historyData = await getCompatibilityHistory(50, 0);
      setHistory(historyData.history || []);
    } catch (error) {
      console.error('Error loading history:', error);
    }
  };

  const handleConnectorSelect = async (connectorId) => {
    if (!connectorId) {
      setSelectedConnector(null);
      setConnectorDetails({});
      return;
    }

    try {
      const details = await getConnectorCompatibility(connectorId);
      setSelectedConnector(connectorId);
      setConnectorDetails(details.connector || {});
    } catch (error) {
      setNotification('Error loading connector details: ' + error.message);
    }
  };

  const handleUpdateCompatibility = async () => {
    if (!selectedConnector) {
      setNotification('Please select a connector first');
      return;
    }

    setLoading(true);
    try {
      await updateConnectorCompatibility(
        selectedConnector,
        connectorDetails.compatibleWith || [],
        connectorDetails.incompatibleWith || [],
        'Manual update via admin panel'
      );
      setNotification('Compatibility updated successfully!');
      loadMatrix();
      loadHistory();
    } catch (error) {
      setNotification('Error updating compatibility: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddConnector = async () => {
    if (!newConnector.id || !newConnector.type) {
      setNotification('Please provide connector ID and type');
      return;
    }

    setLoading(true);
    try {
      await addConnectorToMatrix(newConnector);
      setNotification('Connector added successfully!');
      setNewConnector({
        id: '',
        type: '',
        gender: '',
        brand: '',
        description: '',
        family: '',
        pins: 0,
        skuCode: '',
        verified: false,
        compatibleWith: [],
        incompatibleWith: []
      });
      loadMatrix();
      loadHistory();
    } catch (error) {
      setNotification('Error adding connector: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateConnector = async () => {
    if (!selectedConnector) {
      setNotification('Please select a connector first');
      return;
    }

    setLoading(true);
    try {
      await updateConnectorDetails(selectedConnector, connectorDetails);
      setNotification('Connector updated successfully!');
      loadMatrix();
      loadHistory();
    } catch (error) {
      setNotification('Error updating connector: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConnector = async () => {
    if (!selectedConnector) {
      setNotification('Please select a connector first');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete connector "${selectedConnector}"?`)) {
      return;
    }

    setLoading(true);
    try {
      await deleteConnectorFromMatrix(selectedConnector);
      setNotification('Connector deleted successfully!');
      setSelectedConnector(null);
      setConnectorDetails({});
      loadMatrix();
      loadHistory();
    } catch (error) {
      setNotification('Error deleting connector: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!importData.trim()) {
      setNotification('Please provide import data');
      return;
    }

    setLoading(true);
    try {
      let data;
      if (importFormat === 'json') {
        data = JSON.parse(importData);
      } else {
        data = importData; // CSV data as string
      }

      await importCompatibilityMatrix(data, importFormat, replaceMode);
      setNotification('Matrix imported successfully!');
      setImportData('');
      loadMatrix();
      loadHistory();
    } catch (error) {
      setNotification('Error importing matrix: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format = 'json') => {
    try {
      const data = await exportCompatibilityMatrix(format);
      
      if (format === 'csv') {
        // Download CSV file
        const blob = new Blob([data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `compatibility-matrix-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        // Download JSON file
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `compatibility-matrix-${new Date().toISOString().split('T')[0]}.json`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
      
      setNotification('Matrix exported successfully!');
    } catch (error) {
      setNotification('Error exporting matrix: ' + error.message);
    }
  };

  const handleValidate = async () => {
    setLoading(true);
    try {
      const validationResult = await validateCompatibilityMatrix();
      setValidation(validationResult);
      setNotification(validationResult.valid ? 'Matrix is valid!' : 'Matrix has issues');
    } catch (error) {
      setNotification('Error validating matrix: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const clearNotification = () => {
    setNotification('');
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const connectorList = Object.keys(matrix.connectors || {});

  // Update handleS3Upload
  const handleS3Upload = async () => {
    if (!s3UploadFile) {
      setS3UploadStatus('Please select a file to upload.');
      return;
    }
    setS3UploadStatus('Uploading...');
    const adminToken = getAdminToken();
    if (!adminToken) {
      setS3UploadStatus('Admin token required.');
      return;
    }
    const fileExt = s3UploadFile.name.split('.').pop().toLowerCase();
    const format = fileExt === 'csv' ? 'csv' : 'json';
    const reader = new FileReader();
    reader.onload = async (e) => {
      let matrixData = e.target.result;
      if (format === 'json') {
        try {
          matrixData = JSON.parse(matrixData);
        } catch (err) {
          setS3UploadStatus('Invalid JSON file.');
          return;
        }
      }
      try {
        const res = await fetch('/api/compatibility-matrix?action=import', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'adminToken': adminToken
          },
          body: JSON.stringify({ matrixData, format, replace: true })
        });
        if (!res.ok) {
          const err = await res.json();
          setS3UploadStatus('Upload failed: ' + (err.error || res.status));
          return;
        }
        setS3UploadStatus('Upload complete!');
      } catch (err) {
        setS3UploadStatus('Upload error: ' + err.message);
      }
    };
    reader.onerror = () => setS3UploadStatus('Failed to read file.');
    reader.readAsText(s3UploadFile);
  };

  // Update handleS3Download
  const handleS3Download = async (asCsv = false) => {
    setS3DownloadStatus('Downloading...');
    const adminToken = getAdminToken();
    if (!adminToken) {
      setS3DownloadStatus('Admin token required.');
      return;
    }
    const format = asCsv ? 'csv' : 'json';
    try {
      const res = await fetch(`/api/compatibility-matrix?action=export&format=${format}`, {
        headers: { 'adminToken': adminToken }
      });
      if (!res.ok) {
        const err = await res.json();
        setS3DownloadStatus('Download failed: ' + (err.error || res.status));
        return;
      }
      let blob, filename;
      if (format === 'csv') {
        const text = await res.text();
        blob = new Blob([text], { type: 'text/csv' });
        filename = `compatibility-matrix-${new Date().toISOString().split('T')[0]}.csv`;
      } else {
        const json = await res.json();
        blob = new Blob([JSON.stringify(json, null, 2)], { type: 'application/json' });
        filename = `compatibility-matrix-${new Date().toISOString().split('T')[0]}.json`;
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
      setS3DownloadStatus('Download complete!');
    } catch (err) {
      setS3DownloadStatus('Download error: ' + err.message);
    }
  };

  return (
    <div className="compatibility-admin">
      <h2>Compatibility Matrix Administration</h2>
      {/* Tab Navigation */}
      <div style={{ marginBottom: 24 }}>
        <button
          style={{
            background: activeTab === 'matrix' ? '#007bff' : '#f8f9fa',
            color: activeTab === 'matrix' ? 'white' : '#007bff',
            border: '1px solid #007bff',
            borderRadius: '4px 0 0 4px',
            padding: '10px 24px',
            cursor: 'pointer',
            fontWeight: activeTab === 'matrix' ? 'bold' : 'normal',
          }}
          onClick={() => setActiveTab('matrix')}
        >
          Matrix Management
        </button>
        <button
          style={{
            background: activeTab === 's3' ? '#007bff' : '#f8f9fa',
            color: activeTab === 's3' ? 'white' : '#007bff',
            border: '1px solid #007bff',
            borderLeft: 'none',
            borderRadius: '0 4px 4px 0',
            padding: '10px 24px',
            cursor: 'pointer',
            fontWeight: activeTab === 's3' ? 'bold' : 'normal',
          }}
          onClick={() => setActiveTab('s3')}
        >
          S3 Matrix Upload/Download
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'matrix' && (
        <>
          {notification && (
            <div className={`notification ${notification.includes('Error') ? 'error' : 'success'}`}>
              {notification}
              <button onClick={clearNotification} className="close-btn">×</button>
            </div>
          )}

          <div className="admin-grid">
            {/* Matrix Overview */}
            <div className="admin-section">
              <h3>Matrix Overview</h3>
              <div className="matrix-stats">
                <div className="stat">
                  <span className="stat-label">Total Connectors:</span>
                  <span className="stat-value">{connectorList.length}</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Verified:</span>
                  <span className="stat-value">
                    {connectorList.filter(id => matrix.connectors[id]?.verified).length}
                  </span>
                </div>
                <div className="stat">
                  <span className="stat-label">Last Updated:</span>
                  <span className="stat-value">
                    {matrix.lastUpdated ? formatTimestamp(matrix.lastUpdated) : 'Unknown'}
                  </span>
                </div>
              </div>
              
              <button onClick={handleValidate} disabled={loading}>
                {loading ? 'Validating...' : 'Validate Matrix'}
              </button>
              
              {validation && (
                <div className={`validation-result ${validation.valid ? 'valid' : 'invalid'}`}>
                  <h4>Validation Result</h4>
                  <p>Status: {validation.valid ? 'Valid' : 'Invalid'}</p>
                  <p>Issues: {validation.issues?.length || 0}</p>
                  {validation.summary && (
                    <div className="validation-summary">
                      <p>Total: {validation.summary.totalConnectors}</p>
                      <p>Verified: {validation.summary.verifiedConnectors}</p>
                      <p>With Compatibility: {validation.summary.connectorsWithCompatibility}</p>
                      <p>Without Compatibility: {validation.summary.connectorsWithoutCompatibility}</p>
                    </div>
                  )}
                  {validation.issues && validation.issues.length > 0 && (
                    <div className="validation-issues">
                      <h5>Issues:</h5>
                      <ul>
                        {validation.issues.map((issue, index) => (
                          <li key={index}>{issue}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Connector Selection */}
            <div className="admin-section">
              <h3>Connector Management</h3>
              <div className="form-group">
                <label>Select Connector:</label>
                <select 
                  value={selectedConnector || ''} 
                  onChange={(e) => handleConnectorSelect(e.target.value)}
                >
                  <option value="">-- Select Connector --</option>
                  {connectorList.map(id => (
                    <option key={id} value={id}>{id}</option>
                  ))}
                </select>
              </div>
              
              {selectedConnector && (
                <div className="connector-actions">
                  <button onClick={handleUpdateConnector} disabled={loading}>
                    Update Details
                  </button>
                  <button onClick={handleDeleteConnector} disabled={loading} className="delete-btn">
                    Delete Connector
                  </button>
                </div>
              )}
            </div>

            {/* Connector Details */}
            {selectedConnector && (
              <div className="admin-section">
                <h3>Connector Details: {selectedConnector}</h3>
                <div className="form-group">
                  <label>Type:</label>
                  <input
                    type="text"
                    value={connectorDetails.type || ''}
                    onChange={(e) => setConnectorDetails(prev => ({ ...prev, type: e.target.value }))}
                  />
                </div>
                
                <div className="form-group">
                  <label>Gender:</label>
                  <select
                    value={connectorDetails.gender || ''}
                    onChange={(e) => setConnectorDetails(prev => ({ ...prev, gender: e.target.value }))}
                  >
                    <option value="">-- Select Gender --</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="both">Both</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Brand:</label>
                  <input
                    type="text"
                    value={connectorDetails.brand || ''}
                    onChange={(e) => setConnectorDetails(prev => ({ ...prev, brand: e.target.value }))}
                  />
                </div>
                
                <div className="form-group">
                  <label>Description:</label>
                  <textarea
                    value={connectorDetails.description || ''}
                    onChange={(e) => setConnectorDetails(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                  />
                </div>
                
                <div className="form-group">
                  <label>Family:</label>
                  <input
                    type="text"
                    value={connectorDetails.family || ''}
                    onChange={(e) => setConnectorDetails(prev => ({ ...prev, family: e.target.value }))}
                  />
                </div>
                
                <div className="form-group">
                  <label>Pins:</label>
                  <input
                    type="number"
                    value={connectorDetails.pins || 0}
                    onChange={(e) => setConnectorDetails(prev => ({ ...prev, pins: parseInt(e.target.value) || 0 }))}
                  />
                </div>
                
                <div className="form-group">
                  <label>SKU Code:</label>
                  <input
                    type="text"
                    value={connectorDetails.skuCode || ''}
                    onChange={(e) => setConnectorDetails(prev => ({ ...prev, skuCode: e.target.value }))}
                  />
                </div>
                
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={connectorDetails.verified || false}
                      onChange={(e) => setConnectorDetails(prev => ({ ...prev, verified: e.target.checked }))}
                    />
                    Verified
                  </label>
                </div>
                
                <div className="form-group">
                  <label>Compatible With (comma-separated):</label>
                  <input
                    type="text"
                    value={(connectorDetails.compatibleWith || []).join(', ')}
                    onChange={(e) => setConnectorDetails(prev => ({ 
                      ...prev, 
                      compatibleWith: e.target.value.split(',').map(s => s.trim()).filter(s => s)
                    }))}
                  />
                </div>
                
                <div className="form-group">
                  <label>Incompatible With (comma-separated):</label>
                  <input
                    type="text"
                    value={(connectorDetails.incompatibleWith || []).join(', ')}
                    onChange={(e) => setConnectorDetails(prev => ({ 
                      ...prev, 
                      incompatibleWith: e.target.value.split(',').map(s => s.trim()).filter(s => s)
                    }))}
                  />
                </div>
                
                <button onClick={handleUpdateCompatibility} disabled={loading}>
                  Update Compatibility
                </button>
              </div>
            )}

            {/* Add New Connector */}
            <div className="admin-section">
              <h3>Add New Connector</h3>
              <div className="form-group">
                <label>ID:</label>
                <input
                  type="text"
                  value={newConnector.id}
                  onChange={(e) => setNewConnector(prev => ({ ...prev, id: e.target.value }))}
                  placeholder="Unique connector ID"
                />
              </div>
              
              <div className="form-group">
                <label>Type:</label>
                <input
                  type="text"
                  value={newConnector.type}
                  onChange={(e) => setNewConnector(prev => ({ ...prev, type: e.target.value }))}
                  placeholder="e.g., XLR, TRS, Power"
                />
              </div>
              
              <div className="form-group">
                <label>Gender:</label>
                <select
                  value={newConnector.gender}
                  onChange={(e) => setNewConnector(prev => ({ ...prev, gender: e.target.value }))}
                >
                  <option value="">-- Select Gender --</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="both">Both</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Brand:</label>
                <input
                  type="text"
                  value={newConnector.brand}
                  onChange={(e) => setNewConnector(prev => ({ ...prev, brand: e.target.value }))}
                  placeholder="e.g., Neutrik, Switchcraft"
                />
              </div>
              
              <div className="form-group">
                <label>Description:</label>
                <textarea
                  value={newConnector.description}
                  onChange={(e) => setNewConnector(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Connector description"
                  rows={3}
                />
              </div>
              
              <div className="form-group">
                <label>Family:</label>
                <input
                  type="text"
                  value={newConnector.family}
                  onChange={(e) => setNewConnector(prev => ({ ...prev, family: e.target.value }))}
                  placeholder="e.g., XLR, TRS, Power"
                />
              </div>
              
              <div className="form-group">
                <label>Pins:</label>
                <input
                  type="number"
                  value={newConnector.pins}
                  onChange={(e) => setNewConnector(prev => ({ ...prev, pins: parseInt(e.target.value) || 0 }))}
                />
              </div>
              
              <div className="form-group">
                <label>SKU Code:</label>
                <input
                  type="text"
                  value={newConnector.skuCode}
                  onChange={(e) => setNewConnector(prev => ({ ...prev, skuCode: e.target.value }))}
                  placeholder="e.g., NC3FXX"
                />
              </div>
              
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={newConnector.verified}
                    onChange={(e) => setNewConnector(prev => ({ ...prev, verified: e.target.checked }))}
                  />
                  Verified
                </label>
              </div>
              
              <button onClick={handleAddConnector} disabled={loading}>
                {loading ? 'Adding...' : 'Add Connector'}
              </button>
            </div>

            {/* Import/Export */}
            <div className="admin-section">
              <h3>Import/Export</h3>
              
              <div className="import-section">
                <h4>Import Matrix</h4>
                <div className="form-group">
                  <label>Format:</label>
                  <select
                    value={importFormat}
                    onChange={(e) => setImportFormat(e.target.value)}
                  >
                    <option value="json">JSON</option>
                    <option value="csv">CSV</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={replaceMode}
                      onChange={(e) => setReplaceMode(e.target.checked)}
                    />
                    Replace existing data
                  </label>
                </div>
                
                <div className="form-group">
                  <label>Import Data:</label>
                  <textarea
                    value={importData}
                    onChange={(e) => setImportData(e.target.value)}
                    placeholder={importFormat === 'json' ? 'Paste JSON data here...' : 'Paste CSV data here...'}
                    rows={6}
                  />
                </div>
                
                <button onClick={handleImport} disabled={loading}>
                  {loading ? 'Importing...' : 'Import Matrix'}
                </button>
              </div>
              
              <div className="export-section">
                <h4>Export Matrix</h4>
                <button onClick={() => handleExport('json')} disabled={loading}>
                  Export as JSON
                </button>
                <button onClick={() => handleExport('csv')} disabled={loading}>
                  Export as CSV
                </button>
              </div>
            </div>

            {/* History */}
            <div className="admin-section full-width">
              <h3>Change History</h3>
              <div className="history-list">
                {history.length === 0 ? (
                  <p>No history available</p>
                ) : (
                  history.map((entry, index) => (
                    <div key={index} className="history-item">
                      <div className="history-header">
                        <span className="history-type">{entry.type}</span>
                        <span className="history-time">{formatTimestamp(entry.timestamp)}</span>
                        <span className="history-admin">{entry.adminToken}</span>
                      </div>
                      {entry.connectorId && <div className="history-connector">Connector: {entry.connectorId}</div>}
                      {entry.reason && <div className="history-reason">Reason: {entry.reason}</div>}
                      {entry.changes && (
                        <div className="history-changes">
                          <pre>{JSON.stringify(entry.changes, null, 2)}</pre>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </>
      )}
      {activeTab === 's3' && (
        <div className="admin-section" style={{ maxWidth: 600, margin: '0 auto' }}>
          <h3>S3 Matrix Upload/Download</h3>
          <div className="form-group">
            <label>Upload Matrix File (CSV or JSON):</label>
            <input
              type="file"
              accept=".csv,.json"
              onChange={e => setS3UploadFile(e.target.files[0])}
            />
          </div>
          <button onClick={handleS3Upload} style={{ marginRight: 12 }}>Upload to S3</button>
          {s3UploadStatus && <span style={{ marginLeft: 12 }}>{s3UploadStatus}</span>}
          <hr style={{ margin: '24px 0' }} />
          <button onClick={() => handleS3Download(false)} style={{ marginRight: 12 }}>Download as JSON</button>
          <button onClick={() => handleS3Download(true)}>Download as CSV</button>
          {s3DownloadStatus && <span style={{ marginLeft: 12 }}>{s3DownloadStatus}</span>}
        </div>
      )}

      <style jsx>{`
        .compatibility-admin {
          padding: 20px;
          max-width: 1400px;
          margin: 0 auto;
        }

        .admin-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
          gap: 20px;
          margin-top: 20px;
        }

        .admin-section {
          background: #f8f9fa;
          border: 1px solid #dee2e6;
          border-radius: 8px;
          padding: 20px;
        }

        .admin-section.full-width {
          grid-column: 1 / -1;
        }

        .matrix-stats {
          margin-bottom: 20px;
        }

        .stat {
          display: flex;
          justify-content: space-between;
          padding: 8px 0;
          border-bottom: 1px solid #dee2e6;
        }

        .stat-label {
          font-weight: 600;
        }

        .stat-value {
          color: #007bff;
        }

        .validation-result {
          margin-top: 15px;
          padding: 15px;
          border-radius: 4px;
        }

        .validation-result.valid {
          background: #d4edda;
          border: 1px solid #c3e6cb;
        }

        .validation-result.invalid {
          background: #f8d7da;
          border: 1px solid #f5c6cb;
        }

        .validation-summary {
          margin-top: 10px;
          font-size: 14px;
        }

        .validation-issues ul {
          margin: 10px 0;
          padding-left: 20px;
        }

        .validation-issues li {
          color: #721c24;
          margin-bottom: 5px;
        }

        .form-group {
          margin-bottom: 15px;
        }

        .form-group label {
          display: block;
          margin-bottom: 5px;
          font-weight: 600;
        }

        .form-group input,
        .form-group select,
        .form-group textarea {
          width: 100%;
          padding: 8px;
          border: 1px solid #ced4da;
          border-radius: 4px;
          font-family: inherit;
        }

        .form-group textarea {
          resize: vertical;
        }

        .form-group input[type="checkbox"] {
          width: auto;
          margin-right: 8px;
        }

        .form-group input[type="number"] {
          width: 100px;
        }

        button {
          background: #007bff;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 4px;
          cursor: pointer;
          margin-right: 10px;
          margin-bottom: 10px;
        }

        button:hover {
          background: #0056b3;
        }

        button:disabled {
          background: #6c757d;
          cursor: not-allowed;
        }

        .delete-btn {
          background: #dc3545;
        }

        .delete-btn:hover {
          background: #c82333;
        }

        .connector-actions {
          margin-top: 15px;
        }

        .notification {
          padding: 15px;
          border-radius: 4px;
          margin-bottom: 20px;
          position: relative;
        }

        .notification.success {
          background: #d4edda;
          color: #155724;
          border: 1px solid #c3e6cb;
        }

        .notification.error {
          background: #f8d7da;
          color: #721c24;
          border: 1px solid #f5c6cb;
        }

        .close-btn {
          position: absolute;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          font-size: 18px;
          cursor: pointer;
          color: inherit;
        }

        .import-section,
        .export-section {
          margin-bottom: 20px;
        }

        .history-list {
          max-height: 400px;
          overflow-y: auto;
        }

        .history-item {
          padding: 15px;
          border-bottom: 1px solid #dee2e6;
          background: white;
          margin-bottom: 10px;
          border-radius: 4px;
        }

        .history-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 10px;
        }

        .history-type {
          font-weight: 600;
          color: #007bff;
        }

        .history-time {
          font-size: 12px;
          color: #6c757d;
        }

        .history-admin {
          font-size: 12px;
          color: #6c757d;
          font-family: monospace;
        }

        .history-connector,
        .history-reason {
          font-size: 14px;
          color: #495057;
          margin-bottom: 5px;
        }

        .history-changes pre {
          background: #f8f9fa;
          padding: 10px;
          border-radius: 4px;
          font-size: 12px;
          overflow-x: auto;
          margin-top: 10px;
        }
      `}</style>
    </div>
  );
};

export default CompatibilityMatrixAdmin; 